import { useState, useRef, useCallback, useEffect } from 'react'
import { Mic, Square, Pause, Play, Trash2, Upload } from 'lucide-react'

interface AudioRecorderProps {
  onRecordingComplete: (file: File) => void
  maxDuration?: number // in seconds, default 10 minutes
}

export function AudioRecorder({ onRecordingComplete, maxDuration = 600 }: AudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [duration, setDuration] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl)
    }
  }, [audioUrl])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        }
      })
      streamRef.current = stream

      // Use webm for better compression, fallback to mp4
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/webm'

      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setAudioBlob(blob)
        const url = URL.createObjectURL(blob)
        setAudioUrl(url)

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorder.start(1000) // Collect data every second
      setIsRecording(true)
      setIsPaused(false)
      setDuration(0)
      setAudioBlob(null)
      setAudioUrl(null)

      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= maxDuration) {
            stopRecording()
            return prev
          }
          return prev + 1
        })
      }, 1000)

    } catch (error) {
      console.error('Error accessing microphone:', error)
      alert('Impossibile accedere al microfono. Verifica i permessi del browser.')
    }
  }, [maxDuration])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      setIsPaused(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isRecording])

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume()
        timerRef.current = setInterval(() => {
          setDuration(prev => prev + 1)
        }, 1000)
      } else {
        mediaRecorderRef.current.pause()
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
      setIsPaused(!isPaused)
    }
  }, [isRecording, isPaused])

  const discardRecording = useCallback(() => {
    setAudioBlob(null)
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl)
      setAudioUrl(null)
    }
    setDuration(0)
  }, [audioUrl])

  const uploadRecording = useCallback(() => {
    if (audioBlob) {
      const extension = audioBlob.type.includes('webm') ? 'webm' : 'mp4'
      const fileName = `Registrazione_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.${extension}`
      const file = new File([audioBlob], fileName, { type: audioBlob.type })
      onRecordingComplete(file)
      discardRecording()
    }
  }, [audioBlob, onRecordingComplete, discardRecording])

  // Calculate progress for max duration
  const progress = (duration / maxDuration) * 100

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Mic className="h-5 w-5 text-red-500" />
          Registra Audio
        </h3>
        <span className="text-xs text-slate-500">Max {Math.floor(maxDuration / 60)} minuti</span>
      </div>

      {/* Timer Display */}
      <div className="text-center mb-6">
        <div className={`text-4xl font-mono font-bold transition-colors ${
          isRecording ? (isPaused ? 'text-amber-500' : 'text-red-500') : 'text-slate-400'
        }`}>
          {formatTime(duration)}
        </div>
        {isRecording && (
          <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-1000 ${isPaused ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        {isRecording && !isPaused && (
          <div className="flex items-center justify-center gap-2 mt-2">
            <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-xs text-red-500 font-medium">REC</span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4">
        {!isRecording && !audioBlob && (
          <button
            onClick={startRecording}
            className="bg-red-500 hover:bg-red-600 text-white p-4 rounded-full shadow-lg shadow-red-500/30 transition-all hover:scale-105 active:scale-95"
          >
            <Mic className="h-6 w-6" />
          </button>
        )}

        {isRecording && (
          <>
            <button
              onClick={pauseRecording}
              className={`p-3 rounded-full transition-all hover:scale-105 ${
                isPaused
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
              }`}
            >
              {isPaused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
            </button>
            <button
              onClick={stopRecording}
              className="bg-slate-800 hover:bg-slate-900 text-white p-4 rounded-full shadow-lg transition-all hover:scale-105 active:scale-95"
            >
              <Square className="h-6 w-6" />
            </button>
          </>
        )}

        {audioBlob && !isRecording && (
          <>
            <button
              onClick={discardRecording}
              className="bg-slate-200 hover:bg-slate-300 text-slate-700 p-3 rounded-full transition-all hover:scale-105"
            >
              <Trash2 className="h-5 w-5" />
            </button>
            <button
              onClick={uploadRecording}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-full shadow-lg shadow-emerald-500/30 transition-all hover:scale-105 active:scale-95 flex items-center gap-2 font-medium"
            >
              <Upload className="h-5 w-5" />
              Carica Registrazione
            </button>
          </>
        )}
      </div>

      {/* Audio Preview */}
      {audioUrl && (
        <div className="mt-6">
          <audio src={audioUrl} controls className="w-full" />
          <p className="text-xs text-slate-500 text-center mt-2">
            Dimensione: {(audioBlob!.size / (1024 * 1024)).toFixed(2)} MB
          </p>
        </div>
      )}

      {/* Instructions */}
      {!isRecording && !audioBlob && (
        <p className="text-xs text-slate-500 text-center mt-4">
          Clicca il pulsante rosso per iniziare a registrare
        </p>
      )}
    </div>
  )
}
